import { Controller } from "@hotwired/stimulus"

// Info tooltips on the membership landing page's perk list. Each describes its
// perk in a dark bubble that floats over the list instead of pushing it apart:
//   - a mouse shows it while the pointer rests on the info button;
//   - keyboard focus shows it while the button holds focus;
//   - a tap (touch or pen) toggles it, and a tap anywhere else closes it;
//   - Escape closes it.
// Only one is up at a time — they float over the same list, so a second would
// sit on top of the first.
export default class extends Controller {
  static targets = ["trigger", "panel"]

  connect() {
    this.hovered = null
    this.handleKeydown = this.handleKeydown.bind(this)
    this.handleOutsidePointer = this.handleOutsidePointer.bind(this)
    document.addEventListener("keydown", this.handleKeydown)
    document.addEventListener("pointerdown", this.handleOutsidePointer)
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleKeydown)
    document.removeEventListener("pointerdown", this.handleOutsidePointer)
  }

  hoverStart(event) {
    if (event.pointerType !== "mouse") return

    this.hovered = event.currentTarget
    this.show(event.currentTarget)
  }

  hoverEnd(event) {
    if (event.pointerType !== "mouse") return

    this.hovered = null
    this.hide(event.currentTarget)
  }

  focusStart(event) {
    // A click focuses the button too; the tooltip that click is about is already
    // handled by hover (mouse) or toggle (touch).
    if (!event.currentTarget.matches(":focus-visible")) return

    this.show(event.currentTarget)
  }

  focusEnd(event) {
    if (event.currentTarget === this.hovered) return

    this.hide(event.currentTarget)
  }

  // A mouse already opened the tooltip on hover, so its click changes nothing.
  // A tap, Enter or Space toggles it.
  toggle(event) {
    const trigger = event.currentTarget
    if (trigger === this.hovered) return

    if (this.isOpen(trigger)) {
      this.hide(trigger)
    } else {
      this.show(trigger)
    }
  }

  handleKeydown(event) {
    if (event.key !== "Escape") return

    const trigger = this.triggerTargets.find((candidate) => this.isOpen(candidate))
    if (!trigger) return

    event.preventDefault()
    this.hide(trigger)
  }

  handleOutsidePointer(event) {
    if (this.triggerTargets.some((trigger) => trigger.contains(event.target))) return

    this.triggerTargets.forEach((trigger) => {
      if (trigger !== this.hovered) this.hide(trigger)
    })
  }

  show(trigger) {
    const panel = this.panelFor(trigger)
    if (!panel) return

    this.triggerTargets.forEach((other) => {
      if (other !== trigger) this.hide(other)
    })

    panel.hidden = false
    trigger.classList.add("is-active")
    this.place(trigger, panel)
  }

  hide(trigger) {
    const panel = this.panelFor(trigger)
    if (panel) panel.hidden = true

    trigger.classList.remove("is-active")
  }

  // Centre the bubble under its info button, nudged back inside the viewport when
  // the button sits near an edge, with the notch still pointing at the button.
  place(trigger, panel) {
    const gutter = 16
    const anchor = panel.offsetParent.getBoundingClientRect()
    const buttonCentre = trigger.getBoundingClientRect().left + trigger.offsetWidth / 2
    const width = panel.offsetWidth

    const left = Math.min(
      Math.max(buttonCentre - width / 2, gutter),
      document.documentElement.clientWidth - gutter - width
    )

    panel.style.left = `${left - anchor.left}px`
    panel.style.setProperty("--ms-popover-notch", `${buttonCentre - left}px`)
  }

  isOpen(trigger) {
    return this.panelFor(trigger)?.hidden === false
  }

  panelFor(trigger) {
    const id = trigger.getAttribute("aria-describedby")
    return this.panelTargets.find((panel) => panel.id === id)
  }
}
